/**
 * AdminPage  — /admin
 *
 * Internal MomentSent staff dashboard.
 * Access gated by ADMIN_EMAILS env var on the backend (403 → redirect to dashboard).
 *
 * Tabs:
 *   Overview      — metric cards (users, cards, referrals)
 *   Users         — searchable, filterable user table + user detail drawer
 *   Card Queue    — filter by status; see fulfillment vendor, cost, tracking
 *   Notifications — notification log filtered by type
 *   Jobs          — one-click job triggers with confirmation
 */

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../lib/api';

// ── Types ─────────────────────────────────────────────────────────────────────

interface AdminStats {
  users:     { total: number; active: number; trialing: number; past_due: number; canceled: number };
  cards:     { sent_today: number; sent_this_month: number; sent_this_year: number; failed: number; queued: number };
  referrals: { total: number; converted: number };
  features:  { holiday_blasts: number; group_cards: number };
}

interface AdminEconomics {
  mrr:                       number;
  arr:                       number;
  tier_breakdown:            { tier1: number; tier2: number; tier3: number; trialing: number };
  tier_prices:               { tier1: number; tier2: number; tier3: number };
  cards_sent_this_month:     number;
  card_cost_total:           number;
  avg_card_cost:             number;
  notif_cost_total:          number;
  total_cost_this_month:     number;
  gross_profit_estimate:     number;
  churn_rate_30d:            number;
  churned_last_30d:          number;
  new_subscribers_this_month: number;
  avg_ltv_estimate:          number;
  total_canceled:            number;
}

interface AdminUser {
  id: string; email: string; first_name: string; last_name: string;
  subscription_status: string | null; tier: number | null;
  created_at: string; referral_count: number; app_installed: boolean;
  device_platform: string | null; deleted_at: string | null;
}

interface BroadcastLogEntry {
  id: string; admin_email: string; subject: string; body_preview: string | null;
  target: string; sent_count: number; failed_count: number; created_at: string;
}

interface CardQueueRow {
  id: string; user_id: string; status: string;
  scheduled_send_date: string; sent_at: string | null;
  fulfillment_vendor: string | null; tracking_id: string | null;
  cost_to_company: number | null; retry_count: number;
  card_design_snapshot: { occasion_type?: string } | null;
}

interface NotificationRow {
  id: string; user_id: string; channel: string; notification_type: string;
  vendor: string; status: string; recipient_address: string | null;
  cost_usd: number | null; error_message: string | null; created_at: string;
}

// ── Tab button ────────────────────────────────────────────────────────────────

function Tab({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className={[
        'px-4 py-2 text-sm font-body font-medium rounded-lg transition-colors',
        active
          ? 'bg-navy text-white'
          : 'text-gray-500 hover:text-navy hover:bg-gray-100',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

// ── Metric card ───────────────────────────────────────────────────────────────

function Metric({ label, value, sub, accent = false }: { label: string; value: number | string; sub?: string; accent?: boolean }) {
  return (
    <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
      <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">{label}</p>
      <p className={`font-heading text-3xl font-bold ${accent ? 'text-sage' : 'text-navy'}`}>
        {typeof value === 'number' ? value.toLocaleString() : value}
      </p>
      {sub && <p className="font-body text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

// ── Pagination ────────────────────────────────────────────────────────────────

function Pagination({ page, total, pageSize, onPage }: {
  page: number; total: number; pageSize: number; onPage: (p: number) => void;
}) {
  const pages = Math.ceil(total / pageSize);
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-4 text-xs font-body text-gray-500">
      <span>{total.toLocaleString()} total</span>
      <div className="flex gap-2">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-colors">
          ← Prev
        </button>
        <span className="px-3 py-1.5">{page} / {pages}</span>
        <button disabled={page >= pages} onClick={() => onPage(page + 1)}
          className="px-3 py-1.5 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 transition-colors">
          Next →
        </button>
      </div>
    </div>
  );
}

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    active:   'bg-sage/10 text-sage',
    trialing: 'bg-gold/10 text-[#B59518]',
    past_due: 'bg-red-50 text-red-600',
    canceled: 'bg-gray-100 text-gray-500',
    sent:     'bg-sage/10 text-sage',
    failed:   'bg-red-50 text-red-600',
    queued:   'bg-gold/10 text-[#B59518]',
  };
  return (
    <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${map[status] ?? 'bg-gray-100 text-gray-600'}`}>
      {status}
    </span>
  );
}

// ── Overview tab ──────────────────────────────────────────────────────────────

function OverviewTab({ stats }: { stats: AdminStats }) {
  return (
    <div className="space-y-6">
      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Users</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Metric label="Total" value={stats.users.total} />
          <Metric label="Active"   value={stats.users.active}   accent />
          <Metric label="Trialing" value={stats.users.trialing} />
          <Metric label="Past due" value={stats.users.past_due} />
          <Metric label="Canceled" value={stats.users.canceled} />
        </div>
      </div>

      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Cards</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
          <Metric label="Sent today"       value={stats.cards.sent_today} accent />
          <Metric label="Sent this month"  value={stats.cards.sent_this_month} />
          <Metric label="Sent this year"   value={stats.cards.sent_this_year} />
          <Metric label="Queued"           value={stats.cards.queued} />
          <Metric label="Failed"           value={stats.cards.failed} />
        </div>
      </div>

      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Referrals & Features</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Total referrals"    value={stats.referrals.total} />
          <Metric label="Converted"          value={stats.referrals.converted} accent
            sub={`${stats.referrals.total > 0 ? Math.round((stats.referrals.converted / stats.referrals.total) * 100) : 0}% rate`} />
          <Metric label="Holiday blasts"    value={stats.features.holiday_blasts} />
          <Metric label="Group cards"       value={stats.features.group_cards} />
        </div>
      </div>
    </div>
  );
}

// ── Users tab ─────────────────────────────────────────────────────────────────

function UsersTab() {
  const [users,       setUsers]       = useState<AdminUser[]>([]);
  const [total,       setTotal]       = useState(0);
  const [page,        setPage]        = useState(1);
  const [search,      setSearch]      = useState('');
  const [status,      setStatus]      = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [loading,     setLoading]     = useState(false);
  const [selected,    setSelected]    = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (search)      params.set('search', search);
    if (status)      params.set('status', status);
    if (showDeleted) params.set('show_deleted', 'true');
    const json = await apiFetch<{ users: AdminUser[]; total: number }>(`/api/admin/users?${params}`);
    setUsers(json.users ?? []);
    setTotal(json.total ?? 0);
    setLoading(false);
  }, [page, search, status, showDeleted]);

  useEffect(() => { load(); }, [load]);

  function fmt(d: string) {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap gap-3">
        <input
          type="text"
          placeholder="Search email or name…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          className="input max-w-xs"
        />
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="input max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="active">Active</option>
          <option value="trialing">Trialing</option>
          <option value="past_due">Past due</option>
          <option value="canceled">Canceled</option>
        </select>
        <button onClick={load} className="px-4 py-2 rounded-xl bg-navy text-white text-sm font-body font-medium hover:bg-navy/85 transition-colors">
          Search
        </button>
        <button
          onClick={() => { setShowDeleted(d => !d); setPage(1); }}
          className={`px-4 py-2 rounded-xl text-sm font-body font-medium transition-colors border ${
            showDeleted
              ? 'bg-red-50 text-red-600 border-red-200'
              : 'bg-white text-gray-500 border-gray-200 hover:border-gray-300'
          }`}
        >
          {showDeleted ? '🗑 Showing deleted' : 'Show deleted'}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center font-body text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-gray-100">
                <tr>
                  {['Name', 'Email', 'Status', 'Tier', 'Refs', 'Joined', ''].map((h) => (
                    <th key={h} className="px-4 py-3 font-body text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {users.map((u) => (
                  <tr key={u.id} className={`transition-colors ${u.deleted_at ? 'bg-red-50/30' : 'hover:bg-gray-50/50'}`}>
                    <td className="px-4 py-3 font-body text-sm text-navy whitespace-nowrap">
                      {u.first_name} {u.last_name}
                      {u.deleted_at && (
                        <span className="ml-2 text-[10px] bg-red-100 text-red-500 rounded-full px-2 py-0.5 font-semibold">
                          deleted
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-500">{u.email}</td>
                    <td className="px-4 py-3">
                      <StatusBadge status={u.subscription_status ?? 'none'} />
                    </td>
                    <td className="px-4 py-3 font-body text-sm text-gray-600 text-center">
                      {u.tier ?? ','}
                    </td>
                    <td className="px-4 py-3 font-body text-sm text-gray-600 text-center">
                      {u.referral_count}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-400 whitespace-nowrap">
                      {fmt(u.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setSelected(u.id === selected ? null : u.id)}
                        className="text-xs font-body text-sage hover:underline"
                      >
                        {u.id === selected ? 'Close' : 'View'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Pagination page={page} total={total} pageSize={25} onPage={setPage} />

      {/* Detail drawer */}
      {selected && <UserDetailDrawer userId={selected} onClose={() => setSelected(null)} onDeleted={() => { setSelected(null); load(); }} />}
    </div>
  );
}

// ── User detail drawer ────────────────────────────────────────────────────────

interface StripeSubscription {
  status: string; cancel_at_period_end: boolean;
  current_period_end: string; trial_end: string | null;
}
interface UserDetail {
  user: AdminUser & Record<string, unknown>;
  recent_cards: CardQueueRow[];
  contacts: { id: string; name: string; relationship_type: string | null; address_country: string | null }[];
  notifications: { id: string; channel: string; notification_type: string; status: string; created_at: string }[];
  stripe_subscription: StripeSubscription | null;
}

function ActionButton({ label, onClick, danger = false, disabled = false }: {
  label: string; onClick: () => void; danger?: boolean; disabled?: boolean;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={[
        'px-3 py-2 rounded-xl text-xs font-body font-semibold transition-all disabled:opacity-40',
        danger
          ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100'
          : 'bg-navy/5 text-navy hover:bg-navy/10 border border-navy/10',
      ].join(' ')}
    >
      {label}
    </button>
  );
}

function UserDetailDrawer({ userId, onClose, onDeleted }: { userId: string; onClose: () => void; onDeleted?: () => void }) {
  const [data,      setData]      = useState<UserDetail | null>(null);
  const [loading,   setLoading]   = useState(true);
  const [patch,     setPatch]     = useState<{ tier?: string; subscription_status?: string }>({});
  const [saving,    setSaving]    = useState(false);
  const [msgs,      setMsgs]      = useState<Record<string, string>>({});
  const [busy,      setBusy]      = useState<Record<string, boolean>>({});
  const [refundAmt, setRefundAmt] = useState('');
  const [trialDays, setTrialDays] = useState('7');
  const [copied,    setCopied]    = useState(false);
  const [deleteStep,   setDeleteStep]   = useState<'idle' | 'confirm'>('idle');
  const [cancelConfirm, setCancelConfirm] = useState<'idle' | 'period-end' | 'immediate'>('idle');
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [notifySubject, setNotifySubject] = useState('');
  const [notifyMsg,     setNotifyMsg]     = useState('');

  useEffect(() => {
    apiFetch<UserDetail>(`/api/admin/users/${userId}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [userId]);

  function fmt(d: string) {
    return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
  function setMsg(key: string, msg: string) {
    setMsgs(m => ({ ...m, [key]: msg }));
    setTimeout(() => setMsgs(m => ({ ...m, [key]: '' })), 4000);
  }
  async function act(key: string, fn: () => Promise<void>) {
    setBusy(b => ({ ...b, [key]: true }));
    try { await fn(); } catch (e) { setMsg(key, e instanceof Error ? e.message : 'Error'); }
    setBusy(b => ({ ...b, [key]: false }));
  }

  async function saveEdit() {
    setSaving(true);
    const body: Record<string, unknown> = {};
    if (patch.tier)                body.tier                = parseInt(patch.tier, 10);
    if (patch.subscription_status) body.subscription_status = patch.subscription_status;
    try {
      await apiFetch(`/api/admin/users/${userId}`, { method: 'PATCH', body: JSON.stringify(body) });
      setMsg('edit', 'Saved ✓');
    } catch { setMsg('edit', 'Save failed'); }
    setSaving(false);
  }

  async function cancelSub(immediate: boolean) {
    await act('cancel', async () => {
      await apiFetch(`/api/admin/users/${userId}/cancel-subscription`, {
        method: 'POST', body: JSON.stringify({ immediate }),
      });
      setMsg('cancel', immediate ? 'Cancelled immediately ✓' : 'Set to cancel at period end ✓');
      setCancelConfirm('idle');
    });
  }

  async function issueRefund() {
    const cents = refundAmt ? Math.round(parseFloat(refundAmt) * 100) : undefined;
    if (!confirm(cents ? `Issue $${refundAmt} refund?` : 'Issue full refund of last charge?')) return;
    await act('refund', async () => {
      const res = await apiFetch<{ amount_usd: string }>(`/api/admin/users/${userId}/refund`, {
        method: 'POST', body: JSON.stringify({ amount_cents: cents }),
      });
      setMsg('refund', `Refunded $${res.amount_usd} ✓`);
      setRefundAmt('');
    });
  }

  async function extendTrial() {
    const days = parseInt(trialDays, 10);
    if (!days || days < 1) return;
    await act('trial', async () => {
      await apiFetch(`/api/admin/users/${userId}/extend-trial`, {
        method: 'POST', body: JSON.stringify({ days }),
      });
      setMsg('trial', `Trial extended by ${days} days ✓`);
    });
  }

  async function deleteAccount() {
    await act('delete', async () => {
      await apiFetch(`/api/admin/users/${userId}`, { method: 'DELETE' });
      setMsg('delete', 'Account permanently deleted ✓');
      setDeleteStep('idle');
      setTimeout(() => { onClose(); onDeleted?.(); }, 1500);
    });
  }

  async function sendNotify() {
    if (!notifySubject.trim() || !notifyMsg.trim()) return;
    await act('notify', async () => {
      await apiFetch(`/api/admin/users/${userId}/notify`, {
        method: 'POST',
        body: JSON.stringify({ subject: notifySubject, message: notifyMsg }),
      });
      setMsg('notify', 'Email sent ✓');
      setNotifyOpen(false);
      setNotifySubject('');
      setNotifyMsg('');
    });
  }

  const NOTIFY_TEMPLATES = [
    { label: 'Refund issued',   subject: 'Your refund has been processed', body: 'Hi {{first_name}},\n\nWe have issued a refund to your original payment method. Please allow 5 to 10 business days for it to appear.\n\nIf you have any questions, just reply to this email.\n\nBest,\nThe MomentSent Team' },
    { label: 'Plan changed',    subject: 'Your MomentSent plan has been updated', body: 'Hi {{first_name}},\n\nWe have updated your subscription plan. You can view your current plan details in your account settings.\n\nIf you have any questions, please don\'t hesitate to reach out.\n\nBest,\nThe MomentSent Team' },
    { label: 'Trial extended',  subject: 'Good news, your free trial has been extended', body: 'Hi {{first_name}},\n\nWe have extended your free trial so you have more time to explore everything MomentSent has to offer. No action needed on your end.\n\nEnjoy, and let us know if you have any questions!\n\nBest,\nThe MomentSent Team' },
    { label: 'Account deleted', subject: 'Your MomentSent account has been removed', body: 'Hi there,\n\nYour MomentSent account and all associated data have been permanently removed per your request. No further charges will be made.\n\nIf this was done in error or you have any questions, please email us at hello@momentsent.com.\n\nBest,\nThe MomentSent Team' },
  ];

  const u = data?.user;
  const stripeId = u?.stripe_customer_id as string | null;

  return (
    <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">

      {/* Header */}
      <div className="flex items-start justify-between px-6 pt-5 pb-4 border-b border-gray-100">
        <div>
          {u && <h3 className="font-heading text-lg text-navy">{u.first_name as string} {u.last_name as string}</h3>}
          {u && (
            <div className="flex items-center gap-2 mt-0.5">
              <span className="font-body text-xs text-gray-400">{u.email as string}</span>
              <button
                onClick={() => { navigator.clipboard.writeText(u.email as string); setCopied(true); setTimeout(() => setCopied(false), 2000); }}
                className="font-body text-[10px] text-sage hover:underline"
              >
                {copied ? 'Copied!' : 'Copy'}
              </button>
              {stripeId && (
                <a
                  href={`https://dashboard.stripe.com/customers/${stripeId}`}
                  target="_blank" rel="noopener noreferrer"
                  className="font-body text-[10px] text-sage hover:underline"
                >
                  View in Stripe →
                </a>
              )}
            </div>
          )}
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-navy transition-colors">✕</button>
      </div>

      {loading ? (
        <div className="p-6 space-y-3">
          {[...Array(3)].map((_, i) => <div key={i} className="h-10 animate-pulse bg-gray-100 rounded-xl" />)}
        </div>
      ) : !data ? (
        <p className="p-6 text-sm font-body text-red-500">Failed to load user.</p>
      ) : (
        <div className="divide-y divide-gray-100">

          {/* Info grid */}
          <div className="px-6 py-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              ['Status',   u?.subscription_status ?? 'none'],
              ['Tier',     u?.tier ?? ','],
              ['Contacts', data.contacts.length],
              ['Cards',    data.recent_cards.length],
              ['Joined',   fmt(u?.created_at as string)],
              ['Referrals', u?.referral_count ?? 0],
              ['SMS opted out', u?.sms_opted_out ? 'Yes' : 'No'],
              ['Referral code', u?.referral_code ?? ','],
            ].map(([label, val]) => (
              <div key={label as string} className="bg-gray-50 rounded-xl px-3 py-2">
                <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
                <p className="font-body text-sm text-navy font-medium mt-0.5 truncate">{String(val)}</p>
              </div>
            ))}
            {/* User ID — full UUID for Supabase/Stripe lookups */}
            <div className="bg-gray-50 rounded-xl px-3 py-2 col-span-2">
              <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide">User ID</p>
              <div className="flex items-center gap-2 mt-0.5">
                <p className="font-mono text-[11px] text-navy truncate flex-1">{u?.id as string}</p>
                <button
                  onClick={() => { navigator.clipboard.writeText(u?.id as string); }}
                  className="font-body text-[10px] text-sage hover:underline flex-shrink-0"
                >
                  Copy
                </button>
              </div>
            </div>
          </div>

          {/* Stripe subscription info */}
          {data.stripe_subscription && (
            <div className="px-6 py-4">
              <p className="font-body text-xs font-semibold text-navy mb-2">Stripe subscription</p>
              <div className="flex flex-wrap gap-3 text-xs font-body">
                <span className="bg-gray-50 rounded-lg px-3 py-1.5">
                  Status: <strong>{data.stripe_subscription.status}</strong>
                  {data.stripe_subscription.cancel_at_period_end && <span className="text-red-500 ml-1">(cancels at period end)</span>}
                </span>
                <span className="bg-gray-50 rounded-lg px-3 py-1.5">
                  Period ends: <strong>{fmt(data.stripe_subscription.current_period_end)}</strong>
                </span>
                {data.stripe_subscription.trial_end && (
                  <span className="bg-gold/10 rounded-lg px-3 py-1.5">
                    Trial ends: <strong>{fmt(data.stripe_subscription.trial_end)}</strong>
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="px-6 py-4 space-y-4">
            <p className="font-body text-xs font-semibold text-navy">Actions</p>

            {/* Subscription */}
            <div className="space-y-2">
              <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide">Subscription</p>
              {cancelConfirm === 'idle' ? (
                <div className="flex flex-wrap gap-2 items-center">
                  <ActionButton label="Cancel at period end" onClick={() => setCancelConfirm('period-end')} disabled={busy['cancel']} />
                  <ActionButton label="Cancel immediately"   onClick={() => setCancelConfirm('immediate')}  disabled={busy['cancel']} danger />
                  {msgs['cancel'] && <span className="font-body text-xs text-sage">{msgs['cancel']}</span>}
                </div>
              ) : (
                <div className={`rounded-xl p-3 space-y-2 border ${
                  cancelConfirm === 'immediate'
                    ? 'bg-red-50 border-red-200'
                    : 'bg-amber-50 border-amber-200'
                }`}>
                  <p className={`font-body text-xs font-semibold ${cancelConfirm === 'immediate' ? 'text-red-700' : 'text-amber-700'}`}>
                    {cancelConfirm === 'immediate'
                      ? '⚠ Cancel immediately? Their access ends right now.'
                      : 'Cancel at period end? They keep access until their next billing date.'}
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => cancelSub(cancelConfirm === 'immediate')}
                      disabled={busy['cancel']}
                      className={`px-3 py-1.5 rounded-xl text-xs font-body font-semibold transition-all disabled:opacity-40 ${
                        cancelConfirm === 'immediate'
                          ? 'bg-red-600 text-white hover:bg-red-700'
                          : 'bg-amber-500 text-white hover:bg-amber-600'
                      }`}
                    >
                      {busy['cancel'] ? 'Processing…' : 'Yes, confirm'}
                    </button>
                    <button
                      onClick={() => setCancelConfirm('idle')}
                      className="px-3 py-1.5 rounded-xl bg-white border border-gray-200 text-xs font-body text-gray-600 hover:bg-gray-50 transition-colors"
                    >
                      Never mind
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Refund */}
            <div className="space-y-2">
              <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide">Refund last charge</p>
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-1 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
                  <span className="font-body text-xs text-gray-400">$</span>
                  <input
                    type="number" min="0" step="0.01" placeholder="Leave blank = full"
                    value={refundAmt} onChange={e => setRefundAmt(e.target.value)}
                    className="font-body text-xs text-navy bg-transparent outline-none w-28"
                  />
                </div>
                <ActionButton label={busy['refund'] ? 'Processing…' : 'Issue refund'} onClick={issueRefund} disabled={busy['refund']} danger />
                {msgs['refund'] && <span className="font-body text-xs text-sage">{msgs['refund']}</span>}
              </div>
            </div>

            {/* Extend trial */}
            <div className="space-y-2">
              <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide">Extend trial</p>
              <div className="flex flex-wrap gap-2 items-center">
                <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-3 py-1.5">
                  <input
                    type="number" min="1" max="90"
                    value={trialDays} onChange={e => setTrialDays(e.target.value)}
                    className="font-body text-xs text-navy bg-transparent outline-none w-12"
                  />
                  <span className="font-body text-xs text-gray-400">days</span>
                </div>
                <ActionButton label={busy['trial'] ? 'Extending…' : 'Extend trial'} onClick={extendTrial} disabled={busy['trial']} />
                {msgs['trial'] && <span className="font-body text-xs text-sage">{msgs['trial']}</span>}
              </div>
            </div>

            {/* Edit tier / status */}
            <div className="space-y-2">
              <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide">Edit account</p>
              <div className="flex flex-wrap gap-2 items-end">
                <div>
                  <label className="font-body text-[10px] text-gray-400 block mb-1">Tier</label>
                  <select className="input text-sm py-1.5" defaultValue={String(u?.tier ?? '')}
                    onChange={e => setPatch(p => ({ ...p, tier: e.target.value }))}>
                    <option value="">No change</option>
                    <option value="1">1, Thoughtful</option>
                    <option value="2">2, Connected</option>
                    <option value="3">3, Premium</option>
                  </select>
                </div>
                <div>
                  <label className="font-body text-[10px] text-gray-400 block mb-1">Status</label>
                  <select className="input text-sm py-1.5" defaultValue={u?.subscription_status as string ?? ''}
                    onChange={e => setPatch(p => ({ ...p, subscription_status: e.target.value }))}>
                    <option value="">No change</option>
                    <option value="trialing">trialing</option>
                    <option value="active">active</option>
                    <option value="past_due">past_due</option>
                    <option value="canceled">canceled</option>
                  </select>
                </div>
                <button onClick={saveEdit} disabled={saving}
                  className="px-4 py-2 rounded-xl bg-navy text-white text-xs font-body font-semibold hover:bg-navy/85 transition-colors disabled:opacity-50">
                  {saving ? 'Saving…' : 'Save'}
                </button>
                {msgs['edit'] && <span className="font-body text-xs text-sage">{msgs['edit']}</span>}
              </div>
              <p className="font-body text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2 leading-relaxed">
                ⚠ This updates your database only, Stripe is <strong>not</strong> changed. If the tier change affects billing, update the Stripe subscription separately.
              </p>
            </div>

            {/* ── Delete / Restore ── */}
            <div className="space-y-2 pt-2 border-t border-red-100">
              <p className="font-body text-[10px] text-red-400 uppercase tracking-wide">Danger zone</p>

              {u?.deleted_at ? (
                /* Account is soft-deleted — show restore option */
                <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-3">
                  <p className="font-body text-xs font-semibold text-amber-700">
                    🗑 Account deleted {Math.floor((Date.now() - new Date(u.deleted_at as string).getTime()) / 86400000)} day(s) ago
                    · purges after 30 days
                  </p>
                  <ActionButton
                    label={busy['restore'] ? 'Restoring…' : '↩ Restore account'}
                    onClick={async () => {
                      await act('restore', async () => {
                        await apiFetch(`/api/admin/users/${userId}/restore`, { method: 'POST' });
                        setMsg('restore', 'Account restored ✓, user status set to canceled, re-subscribe required');
                        setTimeout(() => { onClose(); onDeleted?.(); }, 2000);
                      });
                    }}
                    disabled={busy['restore']}
                  />
                  {msgs['restore'] && <span className="font-body text-xs text-sage">{msgs['restore']}</span>}
                </div>
              ) : deleteStep === 'idle' ? (
                <div className="space-y-1">
                  <ActionButton
                    label="🗑 Delete account (30-day recovery)"
                    onClick={() => setDeleteStep('confirm')}
                    danger
                  />
                  <p className="font-body text-[10px] text-gray-400">
                    Cancels Stripe · blocks login · data preserved 30 days then auto-purged
                  </p>
                </div>
              ) : (
                <div className="rounded-xl bg-red-50 border border-red-200 p-4 space-y-3">
                  <p className="font-body text-sm font-semibold text-red-700">
                    ⚠ Delete {u?.first_name as string} {u?.last_name as string}?
                  </p>
                  <p className="font-body text-xs text-red-600 leading-relaxed">
                    Their <strong>Stripe subscription will be cancelled immediately</strong> and they
                    won't be able to log in. Their data is preserved for <strong>30 days</strong> ,
                    you can restore the account during that window if needed.
                  </p>
                  <div className="flex gap-2">
                    <button
                      onClick={deleteAccount}
                      disabled={busy['delete']}
                      className="px-4 py-2 rounded-xl bg-red-600 text-white text-xs font-body font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {busy['delete'] ? 'Deleting…' : 'Yes, delete account'}
                    </button>
                    <button
                      onClick={() => setDeleteStep('idle')}
                      className="px-4 py-2 rounded-xl bg-white text-gray-600 border border-gray-200 text-xs font-body font-semibold hover:bg-gray-50 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                  {msgs['delete'] && (
                    <p className="font-body text-xs text-sage">{msgs['delete']}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ── Notify user by email (optional) ── */}
          <div className="px-6 py-4">
            <button
              type="button"
              onClick={() => setNotifyOpen(o => !o)}
              className="flex items-center gap-2 font-body text-xs font-semibold text-navy hover:text-sage transition-colors"
            >
              <span>{notifyOpen ? '▾' : '▸'}</span>
              📧 Send notification email to user
              <span className="font-normal text-gray-400">(optional)</span>
            </button>

            {notifyOpen && (
              <div className="mt-3 space-y-3">
                {/* Quick-fill templates */}
                <div className="flex flex-wrap gap-1.5">
                  {NOTIFY_TEMPLATES.map(t => (
                    <button
                      key={t.label}
                      type="button"
                      onClick={() => { setNotifySubject(t.subject); setNotifyMsg(t.body); }}
                      className="px-2.5 py-1 rounded-lg bg-gray-100 text-gray-600 text-[10px] font-body hover:bg-navy/10 hover:text-navy transition-colors"
                    >
                      {t.label}
                    </button>
                  ))}
                </div>

                <input
                  type="text"
                  placeholder="Subject"
                  value={notifySubject}
                  onChange={e => setNotifySubject(e.target.value)}
                  className="input w-full text-sm"
                />
                <textarea
                  rows={6}
                  placeholder={`Hi {{first_name}},\n\n...`}
                  value={notifyMsg}
                  onChange={e => setNotifyMsg(e.target.value)}
                  className="input w-full text-xs font-mono resize-y leading-relaxed"
                />
                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={sendNotify}
                    disabled={busy['notify'] || !notifySubject.trim() || !notifyMsg.trim()}
                    className="px-4 py-2 rounded-xl bg-navy text-white text-xs font-body font-semibold hover:bg-navy/85 transition-colors disabled:opacity-40"
                  >
                    {busy['notify'] ? 'Sending…' : 'Send email'}
                  </button>
                  {msgs['notify'] && <span className="font-body text-xs text-sage">{msgs['notify']}</span>}
                </div>
                <p className="font-body text-[10px] text-gray-400">
                  Sent from hello@momentsent.com via SendGrid · {'{{first_name}}'} is replaced automatically
                </p>
              </div>
            )}
          </div>

          {/* Contacts */}
          {data.contacts.length > 0 && (
            <div className="px-6 py-4">
              <p className="font-body text-xs font-semibold text-navy mb-2">Contacts ({data.contacts.length})</p>
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {data.contacts.map(c => (
                  <div key={c.id} className="flex items-center gap-3 text-xs font-body py-1">
                    <span className="text-navy font-medium flex-1 truncate">{c.name}</span>
                    <span className="text-gray-400">{c.relationship_type ?? ','}</span>
                    <span className="text-gray-300">{c.address_country ?? ','}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recent cards */}
          {data.recent_cards.length > 0 && (
            <div className="px-6 py-4">
              <p className="font-body text-xs font-semibold text-navy mb-2">Card history</p>
              <div className="space-y-1">
                {data.recent_cards.map(c => (
                  <div key={c.id} className="flex items-center gap-3 text-xs font-body py-1">
                    <StatusBadge status={c.status} />
                    <span className="text-gray-500">{c.scheduled_send_date}</span>
                    <span className="text-gray-400 capitalize">{c.card_design_snapshot?.occasion_type?.replace(/_/g, ' ') ?? ','}</span>
                    <span className="text-gray-400">{c.fulfillment_vendor ?? ','}</span>
                    {c.cost_to_company != null && <span className="text-gray-400 ml-auto">${c.cost_to_company.toFixed(2)}</span>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notification history */}
          {data.notifications.length > 0 && (
            <div className="px-6 py-4">
              <p className="font-body text-xs font-semibold text-navy mb-2">Notifications sent</p>
              <div className="space-y-1 max-h-36 overflow-y-auto">
                {data.notifications.map(n => (
                  <div key={n.id} className="flex items-center gap-3 text-xs font-body py-1">
                    <StatusBadge status={n.status} />
                    <span className="text-gray-500 capitalize">{n.notification_type.replace(/_/g, ' ')}</span>
                    <span className="text-gray-400 uppercase text-[10px]">{n.channel}</span>
                    <span className="text-gray-300 ml-auto">{new Date(n.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

        </div>
      )}
    </div>
  );
}

// ── Card Queue tab ────────────────────────────────────────────────────────────

function CardQueueTab() {
  const [cards,   setCards]   = useState<CardQueueRow[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [status,  setStatus]  = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (status) params.set('status', status);
    const json = await apiFetch<{ cards: CardQueueRow[]; total: number }>(`/api/admin/card-queue?${params}`);
    setCards(json.cards ?? []);
    setTotal(json.total ?? 0);
    setLoading(false);
  }, [page, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <select
          value={status}
          onChange={(e) => { setStatus(e.target.value); setPage(1); }}
          className="input max-w-[180px]"
        >
          <option value="">All statuses</option>
          <option value="queued">Queued</option>
          <option value="sent">Sent</option>
          <option value="failed">Failed</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <button onClick={load} className="px-4 py-2 rounded-xl bg-navy text-white text-sm font-body font-medium hover:bg-navy/85 transition-colors">
          Filter
        </button>
      </div>

      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center font-body text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-gray-100">
                <tr>
                  {['Status', 'Send date', 'Vendor', 'Occasion', 'Cost', 'Retries', 'Sent at'].map((h) => (
                    <th key={h} className="px-4 py-3 font-body text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {cards.map((c) => (
                  <tr key={c.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
                    <td className="px-4 py-3 font-body text-xs text-gray-600">{c.scheduled_send_date}</td>
                    <td className="px-4 py-3 font-body text-xs text-gray-600">{c.fulfillment_vendor ?? ','}</td>
                    <td className="px-4 py-3 font-body text-xs text-gray-500 capitalize">
                      {c.card_design_snapshot?.occasion_type?.replace(/_/g, ' ') ?? ','}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-600">
                      {c.cost_to_company != null ? `$${c.cost_to_company.toFixed(2)}` : ','}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-600 text-center">{c.retry_count}</td>
                    <td className="px-4 py-3 font-body text-xs text-gray-400">
                      {c.sent_at ? new Date(c.sent_at).toLocaleDateString() : ','}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Pagination page={page} total={total} pageSize={25} onPage={setPage} />
    </div>
  );
}

// ── Notifications tab ─────────────────────────────────────────────────────────

function NotificationsTab() {
  const [rows,    setRows]    = useState<NotificationRow[]>([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [type,    setType]    = useState('');
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams({ page: String(page) });
    if (type) params.set('type', type);
    const json = await apiFetch<{ notifications: NotificationRow[]; total: number }>(`/api/admin/notifications?${params}`);
    setRows(json.notifications ?? []);
    setTotal(json.total ?? 0);
    setLoading(false);
  }, [page, type]);

  useEffect(() => { load(); }, [load]);

  const TYPES = [
    '14day_notification','48hr_reminder','post_send_confirmation',
    'trial_ending','payment_failed','address_bounced',
    'referral_converted','recipient_portal_sms','relationship_wrapped',
    'holiday_blast_confirmation','group_card_invite',
  ];

  return (
    <div className="space-y-4">
      <div className="flex gap-3">
        <select
          value={type}
          onChange={(e) => { setType(e.target.value); setPage(1); }}
          className="input max-w-xs"
        >
          <option value="">All types</option>
          {TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, ' ')}</option>)}
        </select>
        <button onClick={load} className="px-4 py-2 rounded-xl bg-navy text-white text-sm font-body font-medium hover:bg-navy/85 transition-colors">
          Filter
        </button>
      </div>

      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center font-body text-sm text-gray-400">Loading…</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead className="border-b border-gray-100">
                <tr>
                  {['Type', 'Channel', 'Vendor', 'Status', 'Recipient', 'Cost', 'Sent'].map((h) => (
                    <th key={h} className="px-4 py-3 font-body text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {rows.map((n) => (
                  <tr key={n.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-3 font-body text-xs text-navy capitalize max-w-[160px] truncate">
                      {n.notification_type.replace(/_/g, ' ')}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-600">{n.channel}</td>
                    <td className="px-4 py-3 font-body text-xs text-gray-600">{n.vendor}</td>
                    <td className="px-4 py-3"><StatusBadge status={n.status} /></td>
                    <td className="px-4 py-3 font-body text-xs text-gray-400 max-w-[140px] truncate">
                      {n.recipient_address ?? ','}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-500">
                      {n.cost_usd != null ? `$${n.cost_usd.toFixed(4)}` : ','}
                    </td>
                    <td className="px-4 py-3 font-body text-xs text-gray-400 whitespace-nowrap">
                      {new Date(n.created_at).toLocaleDateString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <Pagination page={page} total={total} pageSize={25} onPage={setPage} />
    </div>
  );
}

// ── Economics tab ─────────────────────────────────────────────────────────────

function EconomicsTab() {
  const [data,    setData]    = useState<AdminEconomics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');

  useEffect(() => {
    apiFetch<AdminEconomics>('/api/admin/economics')
      .then(setData)
      .catch(() => setError('Failed to load economics data.'))
      .finally(() => setLoading(false));
  }, []);

  function usd(n: number) {
    return n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });
  }

  if (loading) return <div className="h-32 animate-pulse bg-white rounded-[20px]" />;
  if (error || !data) return <p className="text-sm font-body text-red-500">{error || 'No data.'}</p>;

  const profitColor = data.gross_profit_estimate >= 0 ? 'text-sage' : 'text-red-500';

  return (
    <div className="space-y-6">

      {/* Revenue */}
      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Revenue</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="MRR" value={usd(data.mrr)} accent sub="Monthly recurring revenue" />
          <Metric label="ARR" value={usd(data.arr)} sub="Annualized run rate" />
          <Metric label="New subs (month)" value={data.new_subscribers_this_month} sub="New paying/trialing this month" />
          <Metric label="Est. avg LTV" value={usd(data.avg_ltv_estimate)} sub="Based on 12-mo avg retention" />
        </div>
      </div>

      {/* Tier breakdown */}
      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Active subscribers by tier</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric
            label={`Tier 1 · $${data.tier_prices.tier1}/mo`}
            value={data.tier_breakdown.tier1}
            sub={`≈ ${usd(data.tier_breakdown.tier1 * data.tier_prices.tier1)}/mo`}
          />
          <Metric
            label={`Tier 2 · $${data.tier_prices.tier2}/mo`}
            value={data.tier_breakdown.tier2}
            accent
            sub={`≈ ${usd(data.tier_breakdown.tier2 * data.tier_prices.tier2)}/mo`}
          />
          <Metric
            label={`Tier 3 · $${data.tier_prices.tier3}/mo`}
            value={data.tier_breakdown.tier3}
            sub={`≈ ${usd(data.tier_breakdown.tier3 * data.tier_prices.tier3)}/mo`}
          />
          <Metric label="Trialing" value={data.tier_breakdown.trialing} sub="Not yet paying" />
        </div>
      </div>

      {/* Costs */}
      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Costs this month</h3>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <Metric label="Card fulfillment" value={usd(data.card_cost_total)}
            sub={`${data.cards_sent_this_month} cards · avg ${usd(data.avg_card_cost)}`} />
          <Metric label="Notifications (SMS/email)" value={usd(data.notif_cost_total)} sub="Twilio + SendGrid" />
          <Metric label="Total variable cost" value={usd(data.total_cost_this_month)} />
          <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
            <p className="font-body text-xs text-gray-400 uppercase tracking-wider mb-1">Gross profit est.</p>
            <p className={`font-heading text-3xl font-bold ${profitColor}`}>
              {usd(data.gross_profit_estimate)}
            </p>
            <p className="font-body text-xs text-gray-400 mt-1">MRR minus variable costs</p>
          </div>
        </div>
      </div>

      {/* Retention */}
      <div>
        <h3 className="font-heading text-base font-semibold text-navy mb-3">Retention & churn</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Metric label="Churn rate (30d)" value={`${data.churn_rate_30d}%`}
            sub={`${data.churned_last_30d} canceled in last 30 days`} />
          <Metric label="Total canceled (all time)" value={data.total_canceled} />
          <Metric label="Retention est." value={`${Math.max(0, 100 - data.churn_rate_30d).toFixed(1)}%`}
            accent sub="Monthly retention rate" />
        </div>
        {data.churn_rate_30d > 5 && (
          <p className="mt-3 font-body text-xs text-red-500 bg-red-50 rounded-xl px-4 py-3">
            ⚠ Churn rate above 5%, consider win-back email campaigns or exit surveys.
          </p>
        )}
        {data.churn_rate_30d === 0 && data.tier_breakdown.tier1 + data.tier_breakdown.tier2 + data.tier_breakdown.tier3 > 0 && (
          <p className="mt-3 font-body text-xs text-sage bg-sage/5 rounded-xl px-4 py-3">
            ✓ Zero churn this month, great retention!
          </p>
        )}
      </div>

      <p className="font-body text-xs text-gray-400 text-center">
        MRR and cost estimates refresh each page load · Gross profit excludes fixed costs (hosting, domain, etc.)
      </p>
    </div>
  );
}

// ── Growth tab ────────────────────────────────────────────────────────────────

interface GrowthData {
  monthly_signups:     { month: string; count: number }[];
  monthly_cards:       { month: string; count: number }[];
  monthly_conversions: { month: string; signups: number; converted: number; rate: number }[];
  daily_signups_30d:   { date: string; count: number }[];
  summary: { signups_this_week: number; signups_this_month: number; cards_this_month: number };
}

interface ScenarioPoint { mrr: number; subs: number }
interface ProjectionScenario { label: string; desc: string; m3: ScenarioPoint; m6: ScenarioPoint; m12: ScenarioPoint }
interface ProjectionData {
  current: { mrr: number; subs: number; arpu: number };
  assumptions: {
    avg_monthly_signups: number;
    conversion_rate_pct: number;
    monthly_churn_rate_pct: number;
    avg_new_paying_per_month: number;
    cohort_size: number;
  };
  scenarios: {
    conservative: ProjectionScenario;
    base:         ProjectionScenario;
    optimistic:   ProjectionScenario;
  };
}

function fmtMonth(key: string) {
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [y, m] = key.split('-');
  return `${months[parseInt(m) - 1]} '${y.slice(2)}`;
}

function BarChart({
  data, color, height = 'h-28',
}: {
  data: { label: string; value: number }[];
  color: string;
  height?: string;
}) {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div className={`flex items-end gap-1 ${height} relative`}>
      {data.map((d) => (
        <div key={d.label} className="flex-1 flex flex-col items-center justify-end group">
          <div className="relative w-full flex justify-center">
            {/* Tooltip */}
            <span className="absolute -top-5 left-1/2 -translate-x-1/2 bg-navy text-white
                             text-[9px] font-body px-1.5 py-0.5 rounded opacity-0
                             group-hover:opacity-100 transition-opacity whitespace-nowrap z-10">
              {d.value}
            </span>
            {/* Bar */}
            <div
              className="w-full rounded-t-sm transition-all duration-300"
              style={{
                height: `${Math.max((d.value / max) * 112, d.value > 0 ? 4 : 1)}px`,
                backgroundColor: color,
                opacity: d.value === 0 ? 0.15 : 1,
              }}
            />
          </div>
          <span className="text-[7px] font-body text-gray-400 mt-1 truncate w-full text-center leading-none">
            {d.label}
          </span>
        </div>
      ))}
    </div>
  );
}

function GrowthTab() {
  const [data,        setData]        = useState<GrowthData | null>(null);
  const [projections, setProjections] = useState<ProjectionData | null>(null);
  const [loading,     setLoading]     = useState(true);
  const [error,       setError]       = useState('');

  useEffect(() => {
    Promise.all([
      apiFetch<GrowthData>('/api/admin/growth'),
      apiFetch<ProjectionData>('/api/admin/projections'),
    ])
      .then(([g, p]) => { setData(g); setProjections(p); })
      .catch(() => setError('Failed to load growth data.'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="space-y-4">
      {[...Array(3)].map((_, i) => (
        <div key={i} className="h-40 animate-pulse bg-white rounded-[20px]" />
      ))}
    </div>
  );
  if (error || !data) return <p className="text-sm font-body text-red-500">{error || 'No data.'}</p>;

  const { summary, monthly_signups, monthly_cards, monthly_conversions, daily_signups_30d } = data;

  // Format daily chart — show day-of-month label every 5 days
  const dailyChartData = daily_signups_30d.map((d, i) => ({
    label: i % 5 === 0 ? String(parseInt(d.date.slice(8))) : '',
    value: d.count,
  }));

  const monthlySignupChart = monthly_signups.map(d => ({ label: fmtMonth(d.month), value: d.count }));
  const monthlyCardChart   = monthly_cards.map(d   => ({ label: fmtMonth(d.month), value: d.count }));

  return (
    <div className="space-y-6">

      {/* Summary row */}
      <div className="grid grid-cols-3 gap-3">
        <Metric label="Signups this week"  value={summary.signups_this_week}  accent />
        <Metric label="Signups this month" value={summary.signups_this_month} />
        <Metric label="Cards sent (month)" value={summary.cards_this_month}   />
      </div>

      {/* Daily signups — 30 days */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
        <p className="font-heading text-sm font-semibold text-navy mb-4">
          Daily signups, last 30 days
        </p>
        <BarChart data={dailyChartData} color="#E8C12E" height="h-24" />
      </div>

      {/* Monthly signups — 12 months */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
        <p className="font-heading text-sm font-semibold text-navy mb-4">
          Monthly signups, last 12 months
        </p>
        <BarChart data={monthlySignupChart} color="#E8C12E" />
      </div>

      {/* Monthly cards sent — 12 months */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
        <p className="font-heading text-sm font-semibold text-navy mb-4">
          Cards mailed per month, last 12 months
        </p>
        <BarChart data={monthlyCardChart} color="#1A1410" />
      </div>

      {/* Trial → Paid conversion table */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="font-heading text-sm font-semibold text-navy">
            Trial → paid conversion by month
          </p>
          <p className="font-body text-xs text-gray-400 mt-0.5">
            "Converted" = trial users now active or past_due
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="border-b border-gray-100">
              <tr>
                {['Month', 'Signups', 'Converted', 'Rate', 'Conversion bar'].map(h => (
                  <th key={h} className="px-4 py-3 font-body text-xs font-semibold text-gray-400 uppercase tracking-wide whitespace-nowrap">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {[...monthly_conversions].reverse().map(row => (
                <tr key={row.month} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-4 py-3 font-body text-xs text-navy whitespace-nowrap">{fmtMonth(row.month)}</td>
                  <td className="px-4 py-3 font-body text-sm text-gray-700 text-center">{row.signups}</td>
                  <td className="px-4 py-3 font-body text-sm text-gray-700 text-center">{row.converted}</td>
                  <td className="px-4 py-3 font-body text-sm font-semibold text-center"
                    style={{ color: row.rate >= 50 ? '#E8C12E' : row.rate >= 20 ? '#B59518' : '#6B7280' }}>
                    {row.signups > 0 ? `${row.rate}%` : ','}
                  </td>
                  <td className="px-4 py-3 w-32">
                    {row.signups > 0 && (
                      <div className="w-full bg-gray-100 rounded-full h-1.5">
                        <div
                          className="h-1.5 rounded-full transition-all"
                          style={{ width: `${row.rate}%`, backgroundColor: '#E8C12E' }}
                        />
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Projected Revenue Growth ─────────────────────────────────── */}
      {projections && (
        <div className="space-y-4">

          {/* Header */}
          <div>
            <h3 className="font-heading text-base font-semibold text-navy">
              📈 Projected Revenue Growth
            </h3>
            <p className="font-body text-xs text-gray-400 mt-0.5">
              Based on your actual last 90 days of signup and churn data, not guesses.
            </p>
          </div>

          {/* Assumptions box */}
          <div className="bg-navy/5 border border-navy/10 rounded-[16px] px-5 py-4">
            <p className="font-body text-xs font-semibold text-navy mb-3 uppercase tracking-wide">
              Assumptions used in these projections
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Avg signups / month',    value: projections.assumptions.avg_monthly_signups.toFixed(1),    sub: 'last 90-day avg' },
                { label: 'Trial → paid rate',       value: `${projections.assumptions.conversion_rate_pct}%`,          sub: `${projections.assumptions.cohort_size} user cohort` },
                { label: 'Monthly churn rate',      value: `${projections.assumptions.monthly_churn_rate_pct}%`,       sub: 'last 30 days' },
                { label: 'New paying / month',      value: projections.assumptions.avg_new_paying_per_month.toFixed(1), sub: 'signups × conversion' },
              ].map(a => (
                <div key={a.label} className="bg-white rounded-[12px] px-3 py-2.5 border border-navy/10">
                  <p className="font-body text-[10px] text-gray-400 uppercase tracking-wide leading-tight">{a.label}</p>
                  <p className="font-heading text-xl font-bold text-navy mt-0.5">{a.value}</p>
                  <p className="font-body text-[10px] text-gray-400 mt-0.5">{a.sub}</p>
                </div>
              ))}
            </div>
            <p className="font-body text-[10px] text-gray-400 mt-3">
              ARPU (avg revenue per subscriber): ${projections.current.arpu.toFixed(2)}/mo ·
              Current MRR: ${projections.current.mrr.toLocaleString('en-US', { minimumFractionDigits: 2 })} ·
              Active subscribers: {projections.current.subs}
            </p>
          </div>

          {/* Scenario table */}
          <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b border-gray-100">
              <p className="font-heading text-sm font-semibold text-navy">3 / 6 / 12 month scenarios</p>
              <p className="font-body text-xs text-gray-400 mt-0.5">
                Each scenario compounds month-by-month from your current {projections.current.subs} active subscribers
              </p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="border-b border-gray-100 bg-gray-50/50">
                  <tr>
                    <th className="px-5 py-3 font-body text-xs font-semibold text-gray-400 uppercase tracking-wide w-48">Scenario</th>
                    {['3 months', '6 months', '12 months'].map(h => (
                      <th key={h} className="px-4 py-3 font-body text-xs font-semibold text-gray-400 uppercase tracking-wide text-center">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {([ 'conservative', 'base', 'optimistic' ] as const).map((key) => {
                    const s = projections.scenarios[key];
                    const isBase = key === 'base';
                    const colors: Record<string, string> = {
                      conservative: 'text-gray-500',
                      base:         'text-navy',
                      optimistic:   'text-sage',
                    };
                    return (
                      <tr key={key} className={isBase ? 'bg-navy/[0.02]' : 'hover:bg-gray-50/50 transition-colors'}>
                        <td className="px-5 py-4">
                          <p className={`font-body text-sm font-semibold ${colors[key]}`}>
                            {isBase && <span className="inline-block w-2 h-2 rounded-full bg-navy mr-2 mb-0.5" />}
                            {s.label}
                          </p>
                          <p className="font-body text-[10px] text-gray-400 mt-0.5 leading-snug">{s.desc}</p>
                        </td>
                        {([s.m3, s.m6, s.m12] as ScenarioPoint[]).map((pt, i) => {
                          const diff = pt.mrr - projections.current.mrr;
                          const pct  = projections.current.mrr > 0
                            ? Math.round((diff / projections.current.mrr) * 100)
                            : 0;
                          return (
                            <td key={i} className="px-4 py-4 text-center">
                              <p className={`font-heading text-lg font-bold ${colors[key]}`}>
                                ${pt.mrr.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                              </p>
                              <p className="font-body text-[10px] text-gray-400 mt-0.5">{pt.subs} subs</p>
                              <p className={`font-body text-[10px] font-semibold mt-0.5 ${diff >= 0 ? 'text-sage' : 'text-red-400'}`}>
                                {diff >= 0 ? '+' : ''}{pct}% vs today
                              </p>
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Disclaimer */}
          <p className="font-body text-[11px] text-gray-400 bg-gray-50 rounded-[12px] px-4 py-3 leading-relaxed">
            ⚠️ <strong>These are projections, not guarantees.</strong> They assume your current ARPU
            (${projections.current.arpu.toFixed(2)}/subscriber) stays constant, no pricing changes, and no
            major market shifts. Early-stage companies often see higher variance than these models suggest.
            Revisit monthly as your actual data matures.
          </p>
        </div>
      )}

      <p className="font-body text-xs text-gray-400 text-center">
        Data sourced from your Supabase database · Refreshes each page load ·
        Site traffic & funnel analytics via{' '}
        <a href="https://plausible.io" target="_blank" rel="noopener noreferrer"
           className="text-sage hover:underline">Plausible</a>
      </p>
    </div>
  );
}

// ── Email Broadcast tab ───────────────────────────────────────────────────────

interface BroadcastResult {
  sent:     number;
  skipped:  number;
  preview?: { count: number; sample: string[] };
}

function buildEmailHtml(body: string): string {
  const lines = body
    .split('\n')
    .map((l) => l.trim() ? `<p style="margin:0 0 0.9em">${l}</p>` : '<br/>')
    .join('');
  const year = new Date().getFullYear();
  return `<!DOCTYPE html><html><body style="margin:0;padding:0">
    <div style="background-color:#F8F5EE;padding:32px 16px;font-family:Georgia,serif;color:#1a1a1a;line-height:1.7">
      <div style="max-width:560px;margin:0 auto">
        <div style="text-align:center;padding-bottom:28px;border-bottom:1px solid #E8E0D5;margin-bottom:32px">
          <a href="https://momentsent.com" style="display:inline-block">
            <img src="https://momentsent.com/logo-wordmark.png" alt="MomentSent" width="200" style="height:auto;display:block;margin:0 auto"/>
          </a>
        </div>
        <div style="background:#ffffff;border-radius:16px;padding:32px;box-shadow:0 2px 12px rgba(0,0,0,0.06)">
          ${lines || '<p style="color:#aaa;font-style:italic">Your message will appear here…</p>'}
        </div>
        <div style="text-align:center;margin-top:28px">
          <p style="font-size:12px;color:#9CA3AF;font-family:Arial,sans-serif;margin:0">
            © ${year} MomentSent LLC · Hubert, NC ·
            <a href="https://momentsent.com" style="color:#E8C12E;text-decoration:none">momentsent.com</a>
          </p>
        </div>
      </div>
    </div>
  </body></html>`;
}

function EmailBroadcastTab() {
  const [target,            setTarget]            = useState<'all' | 'active' | 'trialing' | 'past_due'>('all');
  const [subject,           setSubject]           = useState('');
  const [body,              setBody]              = useState('');
  const [previewOnly,       setPreviewOnly]       = useState(true);
  const [previewConfirmed,  setPreviewConfirmed]  = useState(false);
  const [busy,              setBusy]              = useState(false);
  const [result,            setResult]            = useState<BroadcastResult | null>(null);
  const [error,             setError]             = useState('');
  const [history,           setHistory]           = useState<BroadcastLogEntry[]>([]);
  const [showEmailPreview,  setShowEmailPreview]  = useState(false);
  const [historyLoading,    setHistoryLoading]    = useState(true);

  useEffect(() => {
    apiFetch<{ broadcasts: BroadcastLogEntry[] }>('/api/admin/broadcast-log')
      .then(r => setHistory(r.broadcasts ?? []))
      .catch(() => {/* non-fatal */})
      .finally(() => setHistoryLoading(false));
  }, [result]); // re-fetch after every send so history updates

  const TARGET_LABELS: Record<string, string> = {
    all:      'Everyone (all users)',
    active:   'Active subscribers only',
    trialing: 'Trial users only',
    past_due: 'Past-due subscribers only',
  };

  async function send() {
    setError('');
    setResult(null);
    if (!subject.trim()) { setError('Subject is required.'); return; }
    if (!body.trim())    { setError('Email body is required.'); return; }
    if (!previewOnly && !previewConfirmed) {
      setError('Run a successful preview first to unlock sending.');
      return;
    }

    setBusy(true);
    try {
      const res = await apiFetch<BroadcastResult>('/api/admin/broadcast-email', {
        method: 'POST',
        body: JSON.stringify({ target, subject, body, preview_only: previewOnly }),
      });
      setResult(res);
      // Unlock "Send for real" only after a preview with at least 1 recipient
      if (previewOnly && res.preview && res.preview.count > 0) {
        setPreviewConfirmed(true);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to send.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">

      {/* Info banner */}
      <div className="flex items-start gap-3 bg-gold/10 border border-gold/20 rounded-[16px] px-5 py-4">
        <span className="text-xl flex-shrink-0">📢</span>
        <div>
          <p className="font-body text-sm font-semibold text-navy">Broadcast email</p>
          <p className="font-body text-xs text-gray-600 mt-0.5">
            Send a one-time email to all or a segment of your users. Use{' '}
            <code className="bg-white rounded px-1 py-0.5 font-mono text-[11px] text-navy">{'{{first_name}}'}</code>{' '}
            anywhere in the subject or body for personalization.
            Always run a <strong>preview</strong> first.
          </p>
        </div>
      </div>

      {/* Audience */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5 space-y-4">
        <p className="font-heading text-sm font-semibold text-navy">1 · Audience</p>
        <div className="grid grid-cols-2 gap-2">
          {(Object.entries(TARGET_LABELS) as [typeof target, string][]).map(([val, lbl]) => (
            <button
              key={val}
              type="button"
              onClick={() => { setTarget(val); setPreviewConfirmed(false); setResult(null); }}
              className={[
                'text-left px-4 py-3 rounded-xl border text-sm font-body transition-all',
                target === val
                  ? 'bg-navy text-white border-navy font-semibold'
                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:border-navy/30 hover:bg-gray-100',
              ].join(' ')}
            >
              {lbl}
            </button>
          ))}
        </div>
      </div>

      {/* Subject + body */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5 space-y-4">
        <p className="font-heading text-sm font-semibold text-navy">2 · Message</p>

        <div>
          <label className="font-body text-xs text-gray-500 block mb-1.5">
            Subject line
          </label>
          <input
            type="text"
            placeholder="e.g. New feature, Holiday Blast is here 🎉"
            value={subject}
            onChange={e => { setSubject(e.target.value); setPreviewConfirmed(false); setResult(null); }}
            className="input w-full"
          />
        </div>

        <div>
          <label className="font-body text-xs text-gray-500 block mb-1.5">
            Email body{' '}
            <span className="text-gray-400">
              (plain text, line breaks preserved · {'{{first_name}}'} supported)
            </span>
          </label>
          <textarea
            rows={12}
            placeholder={`Hi {{first_name}},\n\nWe just launched something new...\n\nBest,\nThe MomentSent Team`}
            value={body}
            onChange={e => { setBody(e.target.value); setPreviewConfirmed(false); setResult(null); }}
            className="input w-full resize-y font-mono text-xs leading-relaxed"
          />
          <p className="font-body text-[10px] text-gray-400 mt-1">
            {body.length} chars · {body.split(/\s+/).filter(Boolean).length} words
          </p>
        </div>
      </div>

      {/* Live email preview */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        <button
          type="button"
          onClick={() => setShowEmailPreview(v => !v)}
          className="w-full flex items-center justify-between px-5 py-4 text-left hover:bg-gray-50 transition-colors"
        >
          <div>
            <p className="font-heading text-sm font-semibold text-navy">👁 Email preview</p>
            <p className="font-body text-xs text-gray-400 mt-0.5">See exactly what recipients will receive</p>
          </div>
          <span className="font-body text-xs text-gray-400">{showEmailPreview ? '▲ Hide' : '▼ Show'}</span>
        </button>
        {showEmailPreview && (
          <div className="border-t border-gray-100">
            {subject && (
              <div className="px-5 py-3 bg-gray-50 border-b border-gray-100">
                <span className="font-body text-xs text-gray-400">Subject: </span>
                <span className="font-body text-sm text-navy font-medium">{subject || <em className="text-gray-300">No subject yet</em>}</span>
              </div>
            )}
            <iframe
              srcDoc={buildEmailHtml(body)}
              title="Email preview"
              sandbox="allow-same-origin"
              className="w-full border-0"
              style={{ height: 520 }}
            />
          </div>
        )}
      </div>

      {/* Preview / send toggle */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
        <p className="font-heading text-sm font-semibold text-navy mb-3">3 · Send mode</p>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => setPreviewOnly(true)}
            className={[
              'flex-1 py-3 rounded-xl border text-sm font-body font-semibold transition-all',
              previewOnly
                ? 'bg-navy text-white border-navy'
                : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100',
            ].join(' ')}
          >
            🔍 Preview only
            <p className="text-[10px] font-normal mt-0.5 opacity-70">See recipient count, no email sent</p>
          </button>
          <button
            type="button"
            onClick={() => previewConfirmed && setPreviewOnly(false)}
            title={!previewConfirmed ? 'Run a preview first to unlock this' : undefined}
            className={[
              'flex-1 py-3 rounded-xl border text-sm font-body font-semibold transition-all',
              !previewConfirmed
                ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                : !previewOnly
                  ? 'bg-red-600 text-white border-red-600'
                  : 'bg-gray-50 text-gray-600 border-gray-200 hover:bg-red-50 hover:border-red-200 hover:text-red-600',
            ].join(' ')}
          >
            🚀 Send for real
            <p className="text-[10px] font-normal mt-0.5 opacity-70">
              {previewConfirmed ? 'Emails go out immediately, no undo' : '🔒 Run preview first to unlock'}
            </p>
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <p className="font-body text-sm text-red-500 bg-red-50 rounded-[12px] px-4 py-3">
          ⚠ {error}
        </p>
      )}

      {/* Result */}
      {result && (
        <div className={`rounded-[16px] px-5 py-4 space-y-2 ${
          result.preview ? 'bg-navy/5 border border-navy/10' : 'bg-sage/10 border border-sage/20'
        }`}>
          {result.preview ? (
            <>
              <p className="font-heading text-sm font-semibold text-navy">
                👥 Preview result, {result.preview.count} recipient{result.preview.count !== 1 ? 's' : ''} would receive this email
              </p>
              {result.preview.sample.length > 0 && (
                <>
                  <p className="font-body text-xs text-gray-500">Sample recipients:</p>
                  <ul className="space-y-0.5">
                    {result.preview.sample.map(email => (
                      <li key={email} className="font-body text-xs text-gray-600 font-mono">{email}</li>
                    ))}
                  </ul>
                </>
              )}
              <p className="font-body text-xs text-gray-400">
                Ready? Switch to "Send for real" and click Send again.
              </p>
            </>
          ) : (
            <p className="font-heading text-sm font-semibold text-sage">
              ✓ Sent to {result.sent} recipient{result.sent !== 1 ? 's' : ''}
              {result.skipped > 0 && (
                <span className="font-body text-xs text-gray-400 ml-2">
                  ({result.skipped} skipped, no email or unsubscribed)
                </span>
              )}
            </p>
          )}
        </div>
      )}

      {/* Action */}
      <button
        type="button"
        onClick={send}
        disabled={busy || !subject.trim() || !body.trim()}
        className={[
          'w-full py-3.5 rounded-xl font-body font-semibold text-sm transition-all disabled:opacity-40',
          previewOnly
            ? 'bg-navy text-white hover:bg-navy/85'
            : 'bg-red-600 text-white hover:bg-red-700',
        ].join(' ')}
      >
        {busy
          ? (previewOnly ? 'Checking…' : 'Sending…')
          : (previewOnly ? '🔍 Run preview' : `🚀 Send to ${TARGET_LABELS[target]}`)}
      </button>

      <p className="font-body text-[11px] text-gray-400 text-center">
        Emails delivered via SendGrid · batched in groups of 100 · unsubscribe link NOT automatically appended ,
        include one in your copy if required by CAN-SPAM.
      </p>

      {/* ── Broadcast history ── */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="font-heading text-sm font-semibold text-navy">Recent broadcasts</p>
          <p className="font-body text-xs text-gray-400 mt-0.5">Last 20 campaigns sent from the admin panel</p>
        </div>
        {historyLoading ? (
          <div className="p-6 text-center font-body text-xs text-gray-400">Loading history…</div>
        ) : history.length === 0 ? (
          <div className="p-6 text-center font-body text-xs text-gray-400">No broadcasts sent yet.</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {history.map((b) => (
              <div key={b.id} className="px-5 py-3 flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-body text-sm text-navy font-medium truncate">{b.subject}</p>
                  {b.body_preview && (
                    <p className="font-body text-xs text-gray-400 truncate mt-0.5">{b.body_preview}</p>
                  )}
                  <div className="flex flex-wrap gap-2 mt-1">
                    <span className="text-[10px] font-body text-gray-400">
                      Target: <strong className="text-gray-600">{b.target}</strong>
                    </span>
                    <span className="text-[10px] font-body text-sage font-semibold">✓ {b.sent_count} sent</span>
                    {b.failed_count > 0 && (
                      <span className="text-[10px] font-body text-red-500 font-semibold">✗ {b.failed_count} failed</span>
                    )}
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="font-body text-[10px] text-gray-400 whitespace-nowrap">
                    {new Date(b.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' })}
                  </p>
                  <p className="font-body text-[10px] text-gray-400">{b.admin_email}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Jobs tab ──────────────────────────────────────────────────────────────────

interface TestCardResult {
  vendor: string; jobId?: string; costUsd?: string | null;
}

interface DesignOption { id: string; name: string; }

function SendTestCardForm() {
  const [recipientName,  setRecipientName]  = useState('Pete Cote');
  const [street,         setStreet]         = useState('');
  const [city,           setCity]           = useState('');
  const [state,          setState]          = useState('');
  const [zip,            setZip]            = useState('');
  const [occasionType,   setOccasionType]   = useState('birthday');
  const [designId,       setDesignId]       = useState('');
  const [designs,        setDesigns]        = useState<DesignOption[]>([]);
  const [message,        setMessage]        = useState('Hi there,\n\nJust wanted to send you a little something to let you know you\'re being thought of. Hope this card finds you well.');
  const [busy,           setBusy]           = useState<'lob' | 'handwrytten' | null>(null);
  const [result,         setResult]         = useState<{ vendor: string; result: TestCardResult } | null>(null);
  const [error,          setError]          = useState('');

  const OCCASIONS = [
    { value: 'birthday',        label: '🎂 Birthday'      },
    { value: 'anniversary',     label: '💍 Anniversary'   },
    { value: 'christmas',       label: '🎄 Christmas'     },
    { value: 'valentines',      label: '💝 Valentine\'s'  },
    { value: 'thinking_of_you', label: '💌 Just Because'  },
    { value: 'graduation',      label: '🎓 Graduation'    },
  ];

  // Fetch available designs whenever occasion type changes
  useEffect(() => {
    apiFetch<{ designs: DesignOption[] }>(`/api/designs?occasion_type=${occasionType}`)
      .then(json => {
        setDesigns(json.designs ?? []);
        setDesignId(json.designs?.[0]?.id ?? '');
      })
      .catch(() => {});
  }, [occasionType]);

  async function send(vendor: 'lob' | 'handwrytten') {
    setError('');
    setResult(null);
    if (!street.trim() || !city.trim() || !state.trim() || !zip.trim()) {
      setError('Please fill in all address fields.');
      return;
    }
    setBusy(vendor);
    try {
      const res = await apiFetch<TestCardResult>('/api/admin/send-test-card', {
        method: 'POST',
        body: JSON.stringify({
          recipient_name:  recipientName,
          address_street:  street,
          address_city:    city,
          address_state:   state,
          address_zip:     zip,
          address_country: 'US',
          occasion_type:   occasionType,
          card_design_id:  designId || undefined,
          message,
          vendor,
        }),
      });
      setResult({ vendor, result: res });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Send failed.');
    } finally {
      setBusy(null);
    }
  }

  return (
    <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5 space-y-4">
      <div>
        <p className="font-body text-sm font-semibold text-navy">📮 Send Test Card</p>
        <p className="font-body text-xs text-gray-400 mt-1 leading-relaxed">
          Sends a real physical card through the live fulfillment pipeline. This charges the vendor and mails an actual card.
          Every card is a folded greeting card mailed in an envelope, fulfilled by Thankster.
        </p>
      </div>

      {/* Recipient + Occasion */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div>
          <label className="font-body text-[10px] text-gray-400 uppercase tracking-wide block mb-1">Recipient name</label>
          <input
            type="text" value={recipientName} onChange={e => setRecipientName(e.target.value)}
            className="input w-full text-sm"
          />
        </div>
        <div>
          <label className="font-body text-[10px] text-gray-400 uppercase tracking-wide block mb-1">Occasion</label>
          <select value={occasionType} onChange={e => setOccasionType(e.target.value)} className="input w-full text-sm">
            {OCCASIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
      </div>

      {/* Design picker */}
      {designs.length > 0 && (
        <div>
          <label className="font-body text-[10px] text-gray-400 uppercase tracking-wide block mb-1">Card design</label>
          <select value={designId} onChange={e => setDesignId(e.target.value)} className="input w-full text-sm">
            {designs.map(d => <option key={d.id} value={d.id}>{d.name} ({d.id})</option>)}
          </select>
        </div>
      )}

      {/* Address */}
      <div className="space-y-2">
        <label className="font-body text-[10px] text-gray-400 uppercase tracking-wide block">Delivery address</label>
        <input
          type="text" placeholder="Street address" value={street} onChange={e => setStreet(e.target.value)}
          className="input w-full text-sm"
        />
        <div className="grid grid-cols-3 gap-2">
          <input type="text" placeholder="City"  value={city}  onChange={e => setCity(e.target.value)}  className="input text-sm col-span-1" />
          <input type="text" placeholder="State" value={state} onChange={e => setState(e.target.value)} className="input text-sm" />
          <input type="text" placeholder="ZIP"   value={zip}   onChange={e => setZip(e.target.value)}   className="input text-sm" />
        </div>
      </div>

      {/* Message */}
      <div>
        <label className="font-body text-[10px] text-gray-400 uppercase tracking-wide block mb-1">Message inside the card</label>
        <textarea
          rows={6} value={message} onChange={e => setMessage(e.target.value)}
          className="input w-full text-xs font-mono resize-y leading-relaxed"
        />
        <p className="font-body text-[10px] text-gray-400 mt-0.5">{message.length} chars</p>
      </div>

      {/* Error */}
      {error && <p className="font-body text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2">{error}</p>}

      {/* Result */}
      {result && (
        <div className="bg-sage/10 border border-sage/20 rounded-xl px-4 py-3 space-y-1">
          <p className="font-body text-sm font-semibold text-sage">
            ✓ {result.vendor === 'lob' ? 'Standard (Lob)' : 'Handwritten (Handwrytten)'} card submitted successfully
          </p>
          {result.result.jobId && (
            <p className="font-body text-xs text-gray-500">Job ID: <span className="font-mono">{result.result.jobId}</span></p>
          )}
          {result.result.costUsd && (
            <p className="font-body text-xs text-gray-500">Cost: <strong>${result.result.costUsd}</strong></p>
          )}
          <p className="font-body text-xs text-gray-400">Allow 3 to 7 days for delivery.</p>
        </div>
      )}

      {/* Send buttons */}
      <div className="flex flex-wrap gap-3 pt-1">
        <button
          onClick={() => send('lob')}
          disabled={busy !== null}
          className="px-5 py-2.5 rounded-xl bg-navy text-white text-sm font-body font-semibold hover:bg-navy/85 transition-colors disabled:opacity-40"
        >
          {busy === 'lob' ? 'Sending…' : '📬 Send Standard Card (Lob)'}
        </button>
        <button
          onClick={() => send('handwrytten')}
          disabled={busy !== null}
          className="px-5 py-2.5 rounded-xl bg-sage text-navy text-sm font-body font-semibold hover:bg-sage/85 transition-colors disabled:opacity-40"
        >
          {busy === 'handwrytten' ? 'Sending…' : '✍️ Send Handwritten Card (Handwrytten)'}
        </button>
      </div>
      <p className="font-body text-[10px] text-amber-600 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
        ⚠ These are real sends. Each button submits a live order to the vendor and charges your account.
      </p>
    </div>
  );
}

// ── Handwrytten setup panel ───────────────────────────────────────────────────

interface HwDimension { id: number; name: string; width?: number; height?: number; [k: string]: unknown }
interface HwCard      { id: number; name: string; category?: string; [k: string]: unknown }
interface HwFont      { id: number; name: string; [k: string]: unknown }
interface HwSetup {
  instructions: Record<string, string>;
  configured:   Record<string, string>;
  dimensions:   HwDimension[] | { error: string };
  cards:        HwCard[]      | { error: string };
  fonts:        HwFont[]      | { error: string };
}

function HandwryttenSetupPanel() {
  const [loading, setLoading] = useState(false);
  const [data,    setData]    = useState<HwSetup | null>(null);
  const [error,   setError]   = useState<string | null>(null);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch<HwSetup>('/api/admin/handwrytten/setup');
      setData(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  const dims    = data && !('error' in data.dimensions) ? data.dimensions as HwDimension[] : null;
  const cards   = data && !('error' in data.cards)      ? data.cards      as HwCard[]      : null;
  const fonts   = data && !('error' in data.fonts)      ? data.fonts      as HwFont[]      : null;
  const dimErr  = data && 'error' in data.dimensions ? (data.dimensions as { error: string }).error : null;
  const cardErr = data && 'error' in data.cards      ? (data.cards      as { error: string }).error : null;
  const fontErr = data && 'error' in data.fonts      ? (data.fonts      as { error: string }).error : null;

  return (
    <div className="bg-white rounded-[20px] shadow-warm border border-amber-100 p-5 space-y-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="font-body text-sm font-semibold text-navy">✍️ Handwrytten Setup Helper</p>
          <p className="font-body text-xs text-gray-400 mt-1">
            Fetches your Handwrytten account's available card sizes and catalog so you can find
            the IDs you need for Railway env vars.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="flex-shrink-0 px-4 py-2 rounded-xl text-sm font-body font-semibold bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100 transition-colors disabled:opacity-50"
        >
          {loading ? 'Loading…' : 'Check setup'}
        </button>
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-100 px-4 py-3 font-body text-xs text-red-600">
          {error}
        </div>
      )}

      {data && (
        <div className="space-y-4">

          {/* Env var status */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Env vars</p>
            <div className="space-y-1">
              {Object.entries(data.configured).map(([k, v]) => (
                <div key={k} className="flex items-center justify-between gap-4 font-mono text-xs">
                  <span className="text-gray-500">{k}</span>
                  <span className={v.startsWith('✅') ? 'text-sage font-semibold' : 'text-amber-600 font-semibold'}>{v}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Card dimensions */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Card Dimensions
              <span className="ml-2 normal-case font-normal text-gray-400">→ pick the largest flat card, copy its ID → <code className="bg-gray-200 px-1 rounded">HANDWRYTTEN_DIMENSION_ID</code></span>
            </p>
            {dimErr ? (
              <p className="font-body text-xs text-red-500 mt-2">{dimErr}</p>
            ) : dims?.length ? (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {dims.map((d) => (
                  <div key={d.id} className="flex items-center gap-3 font-mono text-xs bg-white rounded-lg px-3 py-1.5 border border-gray-100">
                    <span className="text-sage font-bold w-8">{d.id}</span>
                    <span className="text-gray-700">{d.name}</span>
                    {d.width && d.height && (
                      <span className="text-gray-400 ml-auto">{d.width}×{d.height}</span>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-body text-xs text-gray-400 mt-2">No dimensions returned, check your API key.</p>
            )}
          </div>

          {/* Card catalog */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Card Catalog
              <span className="ml-2 normal-case font-normal text-gray-400">→ pick any clean card, copy its ID → <code className="bg-gray-200 px-1 rounded">HANDWRYTTEN_CARD_ID</code></span>
            </p>
            {cardErr ? (
              <p className="font-body text-xs text-red-500 mt-2">{cardErr}</p>
            ) : cards?.length ? (
              <div className="mt-2 space-y-1 max-h-64 overflow-y-auto">
                {cards.map((c) => (
                  <div key={c.id} className="flex items-center gap-3 font-mono text-xs bg-white rounded-lg px-3 py-1.5 border border-gray-100">
                    <span className="text-sage font-bold w-8">{c.id}</span>
                    <span className="text-gray-700">{c.name}</span>
                    {c.category && <span className="text-gray-400 ml-auto text-[10px]">{c.category}</span>}
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-body text-xs text-gray-400 mt-2">No cards returned, check your API key.</p>
            )}
          </div>

          {/* Fonts */}
          <div className="rounded-xl bg-gray-50 border border-gray-100 p-4">
            <p className="font-body text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
              Handwriting Fonts
              <span className="ml-2 normal-case font-normal text-gray-400">→ copy a font name → <code className="bg-gray-200 px-1 rounded">HANDWRYTTEN_FONT_LABEL</code></span>
            </p>
            {fontErr ? (
              <p className="font-body text-xs text-red-500 mt-2">{fontErr}</p>
            ) : fonts?.length ? (
              <div className="mt-2 space-y-1 max-h-48 overflow-y-auto">
                {fonts.map((f) => (
                  <div key={f.id} className="flex items-center gap-3 font-mono text-xs bg-white rounded-lg px-3 py-1.5 border border-gray-100">
                    <span className="text-sage font-bold w-8">{f.id}</span>
                    <span className="text-gray-700">{f.name}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="font-body text-xs text-gray-400 mt-2">No fonts returned.</p>
            )}
          </div>

        </div>
      )}
    </div>
  );
}

function JobsTab() {
  const [states,      setStates]      = useState<Record<string, 'idle' | 'running' | 'done' | 'error'>>({});
  const [purgeStep,   setPurgeStep]   = useState<'idle' | 'confirm'>('idle');
  const [purgeResult, setPurgeResult] = useState<{ purged: number; total_eligible: number } | null>(null);

  const JOBS = [
    { id: 'notifications', label: 'Notification Engine',       desc: 'Send all pending 14-day and 48-hr card reminders, trial-ending notices, and delivery confirmations.' },
    { id: 'fulfillment',   label: 'Card Fulfillment',          desc: 'Submit all queued cards (status=queued, send date ≤ today) to Lob, PostGrid, or Handwrytten.' },
    { id: 'holiday-blast', label: 'Holiday Blast Fulfillment', desc: 'Fulfill all confirmed (paid) Holiday Blast campaigns. Idempotent, safe to run multiple times.' },
  ];

  async function trigger(jobId: string) {
    setStates((s) => ({ ...s, [jobId]: 'running' }));
    try {
      await apiFetch(`/api/admin/jobs/${jobId}`, { method: 'POST' });
      setStates((s) => ({ ...s, [jobId]: 'done' }));
      setTimeout(() => setStates((s) => ({ ...s, [jobId]: 'idle' })), 4000);
    } catch {
      setStates((s) => ({ ...s, [jobId]: 'error' }));
    }
  }

  async function runPurge() {
    setStates(s => ({ ...s, purge: 'running' }));
    setPurgeResult(null);
    try {
      const res = await apiFetch<{ purged: number; total_eligible: number }>('/api/admin/purge-deleted', { method: 'POST' });
      setPurgeResult(res);
      setStates(s => ({ ...s, purge: 'done' }));
      setPurgeStep('idle');
      setTimeout(() => setStates(s => ({ ...s, purge: 'idle' })), 6000);
    } catch {
      setStates(s => ({ ...s, purge: 'error' }));
      setPurgeStep('idle');
    }
  }

  return (
    <div className="space-y-3">
      <p className="font-body text-sm text-gray-500 mb-2">
        Jobs respond immediately and run asynchronously on the server. Check Railway logs for output.
      </p>
      {JOBS.map((job) => {
        const st = states[job.id] ?? 'idle';
        return (
          <div key={job.id} className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5 flex items-start justify-between gap-4">
            <div>
              <p className="font-body text-sm font-semibold text-navy">{job.label}</p>
              <p className="font-body text-xs text-gray-400 mt-1 leading-relaxed">{job.desc}</p>
            </div>
            <button
              onClick={() => trigger(job.id)}
              disabled={st === 'running'}
              className={[
                'flex-shrink-0 px-4 py-2 rounded-xl text-sm font-body font-semibold transition-all',
                st === 'done'    ? 'bg-sage/10 text-sage'      :
                st === 'error'   ? 'bg-red-50 text-red-600'    :
                st === 'running' ? 'bg-gray-100 text-gray-400' :
                'bg-navy text-white hover:bg-navy/85',
              ].join(' ')}
            >
              {st === 'running' ? 'Running…' :
               st === 'done'    ? 'Started ✓' :
               st === 'error'   ? 'Error ✗'   : 'Run now'}
            </button>
          </div>
        );
      })}

      {/* ── Send test card ── */}
      <SendTestCardForm />

      {/* ── Handwrytten setup helper ── */}
      <HandwryttenSetupPanel />

      {/* ── Purge deleted accounts (danger job) ── */}
      <div className="bg-white rounded-[20px] shadow-warm border border-red-100 p-5 space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-body text-sm font-semibold text-red-700">🗑 Purge Deleted Accounts</p>
            <p className="font-body text-xs text-gray-400 mt-1 leading-relaxed">
              Permanently wipes all accounts soft-deleted more than 30 days ago.
              Removes their contacts, occasions, cards, notifications, and auth record.
              <strong className="text-red-600"> This is irreversible.</strong>
            </p>
          </div>
          {purgeStep === 'idle' && (
            <button
              onClick={() => setPurgeStep('confirm')}
              disabled={states['purge'] === 'running'}
              className={[
                'flex-shrink-0 px-4 py-2 rounded-xl text-sm font-body font-semibold transition-all',
                states['purge'] === 'done'    ? 'bg-sage/10 text-sage'   :
                states['purge'] === 'error'   ? 'bg-red-50 text-red-600' :
                states['purge'] === 'running' ? 'bg-gray-100 text-gray-400' :
                'bg-red-50 text-red-600 hover:bg-red-100 border border-red-200',
              ].join(' ')}
            >
              {states['purge'] === 'running' ? 'Purging…' :
               states['purge'] === 'done'    ? 'Done ✓' :
               states['purge'] === 'error'   ? 'Error ✗' : 'Run purge'}
            </button>
          )}
        </div>

        {purgeStep === 'confirm' && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-4 py-3 space-y-2">
            <p className="font-body text-xs font-semibold text-red-700">
              ⚠ Permanently delete all accounts soft-deleted 30+ days ago?
              All their data will be gone forever.
            </p>
            <div className="flex gap-2">
              <button
                onClick={runPurge}
                disabled={states['purge'] === 'running'}
                className="px-4 py-1.5 rounded-xl bg-red-600 text-white text-xs font-body font-semibold hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {states['purge'] === 'running' ? 'Purging…' : 'Yes, purge now'}
              </button>
              <button
                onClick={() => setPurgeStep('idle')}
                className="px-4 py-1.5 rounded-xl bg-white border border-gray-200 text-xs font-body text-gray-600 hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {purgeResult && (
          <p className="font-body text-xs text-sage font-semibold">
            ✓ Purged {purgeResult.purged} account{purgeResult.purged !== 1 ? 's' : ''}
            {purgeResult.total_eligible > purgeResult.purged
              ? ` (${purgeResult.total_eligible - purgeResult.purged} failed, check Railway logs)`
              : ' · all eligible accounts removed'}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

// ── Churn tab ─────────────────────────────────────────────────────────────────

interface ChurnEntry {
  id: string; user_email: string; tier: number | null;
  reason: string; comment: string | null; created_at: string;
}

function ChurnTab() {
  const [entries,      setEntries]      = useState<ChurnEntry[]>([]);
  const [reasonCounts, setReasonCounts] = useState<Record<string, number>>({});
  const [total,        setTotal]        = useState(0);
  const [loading,      setLoading]      = useState(true);

  useEffect(() => {
    apiFetch<{ entries: ChurnEntry[]; reasonCounts: Record<string, number>; total: number }>('/api/admin/churn')
      .then(r => { setEntries(r.entries); setReasonCounts(r.reasonCounts); setTotal(r.total); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const TIER_NAMES: Record<number, string> = { 1: 'Thoughtful', 2: 'Connected', 3: 'Premium' };

  if (loading) return <div className="py-12 text-center font-body text-sm text-gray-400">Loading…</div>;

  return (
    <div className="space-y-6 max-w-3xl">

      {/* Summary banner */}
      <div className="flex items-center gap-3 bg-rose-50 border border-rose-100 rounded-[16px] px-5 py-4">
        <span className="text-2xl">📉</span>
        <div>
          <p className="font-body text-sm font-semibold text-navy">
            {total} cancellation{total !== 1 ? 's' : ''} recorded
          </p>
          <p className="font-body text-xs text-gray-500 mt-0.5">
            Feedback collected from users who clicked "Cancel subscription"
          </p>
        </div>
      </div>

      {/* Reason breakdown */}
      {Object.keys(reasonCounts).length > 0 && (
        <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-5">
          <p className="font-heading text-sm font-semibold text-navy mb-4">Reason breakdown</p>
          <div className="space-y-3">
            {Object.entries(reasonCounts)
              .sort(([, a], [, b]) => b - a)
              .map(([reason, count]) => {
                const pct = Math.round((count / total) * 100);
                return (
                  <div key={reason}>
                    <div className="flex justify-between mb-1">
                      <span className="font-body text-sm text-gray-700">{reason}</span>
                      <span className="font-body text-sm font-semibold text-navy">{count} <span className="text-gray-400 font-normal">({pct}%)</span></span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                      <div className="h-2 bg-rose-400 rounded-full" style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
          </div>
        </div>
      )}

      {/* Individual entries */}
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <p className="font-heading text-sm font-semibold text-navy">All feedback</p>
          <p className="font-body text-xs text-gray-400 mt-0.5">Most recent first</p>
        </div>
        {entries.length === 0 ? (
          <div className="p-8 text-center font-body text-sm text-gray-400">No cancellation feedback yet.</div>
        ) : (
          <div className="divide-y divide-gray-50">
            {entries.map(e => (
              <div key={e.id} className="px-5 py-4 space-y-1">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-body text-sm font-semibold text-rose-600">{e.reason}</span>
                  <div className="flex items-center gap-2">
                    {e.tier && (
                      <span className="font-body text-xs bg-navy/10 text-navy px-2 py-0.5 rounded-full">
                        {TIER_NAMES[e.tier] ?? `Tier ${e.tier}`}
                      </span>
                    )}
                    <span className="font-body text-xs text-gray-400">
                      {new Date(e.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </span>
                  </div>
                </div>
                <p className="font-body text-xs text-gray-500 font-mono">{e.user_email}</p>
                {e.comment && (
                  <p className="font-body text-sm text-gray-700 italic mt-1">"{e.comment}"</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Traffic tab (embedded Plausible dashboard) ─────────────────────────────────
// Reads the Plausible "Shared Link" URL from VITE_PLAUSIBLE_SHARED_URL so the
// auth token is never committed to source. Create the link in Plausible under
// Site Settings → Visibility → Shared Links, then set the env var (locally in
// web/.env and on the Railway frontend service).

function TrafficTab() {
  const sharedUrl = import.meta.env.VITE_PLAUSIBLE_SHARED_URL as string | undefined;

  useEffect(() => {
    if (!sharedUrl) return;
    const id = 'plausible-embed-host';
    if (document.getElementById(id)) return;
    const s = document.createElement('script');
    s.id = id;
    s.async = true;
    s.src = 'https://plausible.io/js/embed.host.js';
    document.body.appendChild(s);
  }, [sharedUrl]);

  if (!sharedUrl) {
    return (
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-8 max-w-2xl">
        <h2 className="font-heading text-xl font-bold text-navy mb-2">Live site traffic</h2>
        <p className="font-body text-sm text-gray-600 leading-relaxed mb-6">
          Visits, traffic sources, and visitor locations are tracked by Plausible. View the full
          dashboard below.
        </p>
        <a
          href="https://plausible.io/momentsent.com"
          target="_blank"
          rel="noreferrer"
          className="inline-block bg-navy text-white font-body font-semibold text-sm px-6 py-3 rounded-xl hover:bg-navy/90 transition-colors mb-6"
        >
          🌐 View Live Traffic Dashboard →
        </a>
        <div className="border-t border-gray-100 pt-5">
          <p className="font-body text-xs text-gray-400 leading-relaxed">
            The button opens your Plausible dashboard. To make it viewable, turn on
            <span className="font-medium text-gray-500"> Site Settings → Visibility → "Make stats publicly available"</span>
            {' '}in Plausible (free). To embed the dashboard inside this page instead, Plausible
            requires a paid plan, once upgraded, create a Shared Link and set it as
            {' '}<code className="bg-gray-100 px-1 rounded">VITE_PLAUSIBLE_SHARED_URL</code> on the
            Railway frontend service.
          </p>
        </div>
      </div>
    );
  }

  const embedSrc = `${sharedUrl}${sharedUrl.includes('?') ? '&' : '?'}embed=true&theme=light`;

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="font-body text-sm text-gray-500">
          Live site traffic, sources, and locations via Plausible.
        </p>
        <a
          href={sharedUrl}
          target="_blank"
          rel="noreferrer"
          className="font-body text-xs text-sage hover:underline"
        >
          Open full dashboard →
        </a>
      </div>
      <div className="bg-white rounded-[20px] shadow-warm border border-gray-100 p-2 overflow-hidden">
        <iframe
          plausible-embed=""
          src={embedSrc}
          title="MomentSent traffic"
          loading="lazy"
          scrolling="no"
          frameBorder={0}
          style={{ width: '1px', minWidth: '100%', height: 1750 }}
        />
      </div>
    </div>
  );
}

type TabId = 'overview' | 'economics' | 'growth' | 'traffic' | 'users' | 'card-queue' | 'notifications' | 'jobs' | 'email-broadcast' | 'churn';

export default function AdminPage() {
  const navigate = useNavigate();
  const [stats,   setStats]   = useState<AdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [denied,  setDenied]  = useState(false);
  const [tab,     setTab]     = useState<TabId>('overview');

  useEffect(() => {
    (async () => {
      try {
        const json = await apiFetch<AdminStats>('/api/admin/stats');
        setStats(json);
      } catch (err) {
        const msg = err instanceof Error ? err.message : '';
        if (msg.includes('401')) { navigate('/login', { replace: true }); return; }
        // 403 or any other admin-access error
        setDenied(true);
      } finally {
        setLoading(false);
      }
    })();
  }, [navigate]);

  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="w-8 h-8 rounded-full border-2 border-navy border-t-transparent animate-spin" />
      </div>
    );
  }

  if (denied) {
    return (
      <div className="min-h-screen bg-cream flex flex-col items-center justify-center p-6 text-center">
        <div className="text-5xl mb-4">🚫</div>
        <h1 className="font-heading text-2xl text-navy mb-2">Admin access only</h1>
        <p className="font-body text-sm text-gray-500 mb-6">
          Your account doesn't have admin privileges.
        </p>
        <a href="/dashboard" className="font-body text-sm text-sage hover:underline">
          ← Back to dashboard
        </a>
      </div>
    );
  }

  const TABS: { id: TabId; label: string }[] = [
    { id: 'overview',       label: '📊 Overview'      },
    { id: 'economics',      label: '💰 Economics'     },
    { id: 'growth',         label: '📈 Growth'        },
    { id: 'traffic',        label: '🌐 Traffic'        },
    { id: 'users',          label: '👥 Users'          },
    { id: 'card-queue',     label: '📬 Card Queue'     },
    { id: 'notifications',  label: '🔔 Notifications'  },
    { id: 'jobs',           label: '⚡ Jobs'            },
    { id: 'email-broadcast', label: '📧 Broadcast'     },
    { id: 'churn',           label: '📉 Churn'          },
  ];

  return (
    <div className="min-h-screen bg-cream">
      {/* Top nav */}
      <div className="sticky top-0 z-20 bg-cream/95 backdrop-blur-sm border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <a href="/dashboard" className="font-heading text-base font-semibold text-navy hover:opacity-70 transition-opacity">
              MomentSent
            </a>
            <span className="text-gray-300">/</span>
            <span className="font-body text-sm text-gray-500 font-medium">Admin</span>
          </div>
          <span className="font-body text-xs text-gray-400 hidden sm:block">
            Internal, not for public access
          </span>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Page title */}
        <h1 className="font-heading text-2xl font-bold text-navy mb-6">Admin Dashboard</h1>

        {/* Tab bar */}
        <div className="flex flex-wrap gap-2 mb-6 p-1 bg-white rounded-[16px] shadow-warm border border-gray-100 w-fit">
          {TABS.map((t) => (
            <Tab key={t.id} label={t.label} active={tab === t.id} onClick={() => setTab(t.id)} />
          ))}
        </div>

        {/* Tab content */}
        {tab === 'overview'      && stats && <OverviewTab stats={stats} />}
        {tab === 'economics'     && <EconomicsTab />}
        {tab === 'growth'        && <GrowthTab />}
        {tab === 'traffic'       && <TrafficTab />}
        {tab === 'users'         && <UsersTab />}
        {tab === 'card-queue'    && <CardQueueTab />}
        {tab === 'notifications' && <NotificationsTab />}
        {tab === 'jobs'           && <JobsTab />}
        {tab === 'email-broadcast' && <EmailBroadcastTab />}
        {tab === 'churn'           && <ChurnTab />}
      </div>
    </div>
  );
}
